import { createContext, useContext, useState, useLayoutEffect } from "react";

type MobileViewportResult = { isMobileViewport: boolean; isPending: boolean };

/**
 * When provided, overrides the return value of useIsMobileViewport.
 * Used in Storybook to force mobile/desktop layout inside `<Canvas>` embeds
 * where the iframe is not resized to match the viewport global.
 */
const MobileViewportOverrideContext = createContext<MobileViewportResult | null>(null);
export const MobileViewportOverrideProvider = MobileViewportOverrideContext.Provider;

/**
 * A hook that detects if the current viewport is mobile-sized using matchMedia
 * @param query - The media query to match (default: '(max-width: 767px)')
 * @returns True if the media query matches. It also returns a pending state which is true until the useLayoutEffect has run.
 */
const useIsMobileViewport = (query = "(max-width: 767px)") => {
    const override = useContext(MobileViewportOverrideContext);

    const [matches, setMatches] = useState(false);
    const [isPending, setIsPending] = useState(true);

    useLayoutEffect(() => {
        const mediaQuery = window.matchMedia(query);
        setMatches(mediaQuery.matches);
        setIsPending(false);

        mediaQuery.onchange = (event) => {
            setMatches(event.matches);
        };

        return () => {
            mediaQuery.onchange = null;
        };
    }, [query]);

    if (override) {
        return override;
    }

    return { isMobileViewport: matches, isPending: isPending };
};

export default useIsMobileViewport;
